import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { ResetIcon } from "@/assets/icons";
import { Switch } from "@/components/ui/switch";
import {
  DEFAULT_STEP,
  MAX_STEP,
  MIN_STEP,
  OTHER_ROW_ID
} from "@/constants/dataset";
import { RootState } from "@/store";
import {
  CategoryRow,
  CustomCategoryState,
  CustomCategoryValue,
  DatasetAttributeSchema,
  DraftState,
  EColorNames,
  RowMarkerFields
} from "@/types/dataset";
import {
  customCategoryPaletteColor,
  getAttributeTableValues,
  getDefaultCustomCategoryConfig
} from "@/utils/dataset";

import EditCategoryPanel from "./EditCategoryPanel";
import {
  LegendActions,
  LegendColumnHeaders,
  LegendOtherRow,
  LegendRow,
  LegendRowActivePanel
} from "./LegendRow";
import { useLegendSubPanels } from "./useLegendSubPanels";

interface CustomCategoryConfigProps {
  isPOI?: boolean;
  /** Line datasets: single "Stroke" color column (no Fill/outline), drives line-color. */
  strokeOnly?: boolean;
  disabled?: boolean;
  availableValues?: string[];
  datasetId?: string;
  selectedCategory?: DatasetAttributeSchema;
  colorSteps?: number;
  colorFamily?: EColorNames;
  value?: CustomCategoryValue;
  onPreview?: (value: CustomCategoryValue) => void;
  onSave?: (value: CustomCategoryValue) => void | Promise<boolean | void>;
  onCancel?: () => void;
  onReset?: () => CustomCategoryValue | null;
  /** Restores a config the store no longer holds: a Color Scale step change
   * resizes the categories over the saved baseline, so Cancel hands back the
   * panel's own pre-change copy. */
  onRevert?: (value: CustomCategoryValue) => void | Promise<boolean | void>;
  /** Reports the step count a row deletion leaves behind, so the Color Scale
   * step input follows the category list. */
  onStepCountChange?: (step: number) => void;
}

const mapValueToState = (value: CustomCategoryValue): CustomCategoryState => ({
  rows: value.steps.map((step, index) => ({
    id: `cat-${index}`,
    name: step.label,
    values: step.values,
    fillColor: step.fillColor,
    outlineColor: step.outlineColor,
    image: step.imageUrl,
    imageName: step.imageName,
    imageAssetId: step.imageAssetId,
    iconId: step.iconId ?? null
  })),
  otherFill: value.otherFillColor,
  otherOutline: value.otherOutlineColor
});

/** The Color Scale step count a category list needs: one per row, plus the
 * "Other" slot. */
const stepCountForRows = (rowCount: number): number =>
  Math.min(Math.max(rowCount + 1, MIN_STEP), MAX_STEP);

const mapStateToValue = (state: CustomCategoryState): CustomCategoryValue => ({
  steps: state.rows.map((row) => ({
    label: row.name,
    values: row.values,
    fillColor: row.fillColor,
    outlineColor: row.outlineColor,
    imageUrl: row.image,
    imageName: row.imageName,
    imageAssetId: row.imageAssetId,
    iconId: row.iconId
  })),
  otherFillColor: state.otherFill,
  otherOutlineColor: state.otherOutline
});

const CustomCategoryConfig = ({
  isPOI = false,
  strokeOnly = false,
  disabled = false,
  availableValues,
  datasetId,
  selectedCategory,
  colorSteps = DEFAULT_STEP,
  colorFamily = EColorNames.TEAL,
  value,
  onPreview,
  onSave,
  onCancel,
  onReset,
  onRevert,
  onStepCountChange
}: CustomCategoryConfigProps) => {
  const [useImage, setUseImage] = useState<boolean>(() =>
    Boolean(value?.steps?.some((step) => step.imageUrl || step.iconId))
  );
  const lastMarkersRef = useRef<Map<string, RowMarkerFields> | null>(null);
  // The config this panel last previewed. It comes back as a `value` change, and
  // without recognising it as our own the rebuilt `base` would discard the very
  // edit that triggered it. Each preview is a fresh object, so it cannot collide.
  const previewedValueRef = useRef<CustomCategoryValue | null>(null);
  // The step the panel just announced, waiting for its prop echo. It is consumed
  // on arrival: left armed, a later move back to the same number would read as
  // ours and the panel would stop following the store.
  const [pendingSelfStep, setPendingSelfStep] = useState<number | null>(null);
  // The step in effect before this editing session's first step change, so
  // Cancel can put it back. The step is the user's own pick, not derived from
  // the rows, so only the pre-edit value can restore it.
  const [stepBeforeEdit, setStepBeforeEdit] = useState<number | null>(null);
  const [draftState, setDraftState] = useState<DraftState | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  // Announces a step the panel itself decided, so the prop echo is not mistaken
  // for the user moving the Color Scale step input.
  const emitStepCount = (step: number) => {
    setPendingSelfStep(step);
    onStepCountChange?.(step);
  };

  const rawDataset = useSelector((state: RootState) =>
    datasetId ? state.dataset.rawDatasets?.[datasetId] : undefined
  );
  const tableValues = useMemo(
    () => getAttributeTableValues(rawDataset, selectedCategory?.attribute_name),
    [rawDataset, selectedCategory]
  );

  const allValues = useMemo(() => {
    if (tableValues.length) return tableValues;
    if (availableValues?.length) return availableValues;
    return [];
  }, [tableValues, availableValues]);

  const isLoadingValues = false;

  const externalBase = useMemo((): CustomCategoryState => {
    if (!value)
      return mapValueToState(
        getDefaultCustomCategoryConfig(colorFamily, colorSteps, tableValues)
      );
    const hydrated = mapValueToState(value);
    if (!tableValues.length) return hydrated;
    const available = new Set(tableValues);
    const rows = hydrated.rows.map((row) => {
      const kept = row.values.filter((v) => available.has(v));
      return kept.length === row.values.length ? row : { ...row, values: kept };
    });
    return { ...hydrated, rows };
  }, [value, colorFamily, colorSteps, tableValues]);

  // Unsaved edits stay live only while they branch from the CURRENT `base`.
  // An external palette or data change rebuilds `base`, discarding the
  // now-stale draft. Changes the panel caused itself, and the store's resize
  // for a new step count, leave `base` — and with it the draft — alone.
  const [base, setBase] = useState<CustomCategoryState>(externalBase);
  const [lastExternalBase, setLastExternalBase] =
    useState<CustomCategoryState>(externalBase);
  const [lastColorSteps, setLastColorSteps] = useState(colorSteps);
  const [lastValue, setLastValue] = useState(value);
  // True while `base` is the only copy left of the pre-change config: a step
  // change resizes the categories over the store's saved baseline.
  const [hasStaleBaseline, setHasStaleBaseline] = useState(false);

  // Closes the editing session: whatever arrives next counts as external again.
  const endEditingSession = () => {
    previewedValueRef.current = null;
    setPendingSelfStep(null);
    setStepBeforeEdit(null);
    setHasStaleBaseline(false);
  };

  if (externalBase !== lastExternalBase || colorSteps !== lastColorSteps) {
    // Step and value provenance are tracked separately. A step the panel
    // announced must not suppress a config somebody else committed in the same
    // render: Reset commits the default AND announces its step, so a combined
    // check left the panel showing the pre-reset categories.
    const isOwnStep =
      pendingSelfStep !== null && colorSteps === pendingSelfStep;
    const isOwnValue = value === previewedValueRef.current;
    // Guarded on `value` itself — a step change alone rebuilds `externalBase`.
    const valueChanged = value !== lastValue;
    const stepBefore = lastColorSteps;
    setLastExternalBase(externalBase);
    setLastColorSteps(colorSteps);
    setLastValue(value);
    // Our step reaches the props exactly once, so disarm it here.
    if (isOwnStep) setPendingSelfStep(null);

    // An own value echo needs nothing — `base` and the draft already account for
    // it — so only configs somebody else wrote reach the branches below.
    if (!isOwnValue) {
      if (isOwnStep) {
        // Our step, but a config we did not write: adopt it as the new baseline.
        if (valueChanged) setBase(externalBase);
      } else if (colorSteps !== stepBefore || hasStaleBaseline) {
        // The user moved the Color Scale step input and the store resized the
        // categories for it. That resize is a pending edit, not a new baseline,
        // so it becomes the draft — Save commits it, Cancel puts the old one
        // back. `hasStaleBaseline` also catches the rest of a resize reaching
        // the panel in a later render than the step itself.
        if (stepBeforeEdit === null) setStepBeforeEdit(stepBefore);
        setHasStaleBaseline(true);
        setDraftState({
          base,
          state: externalBase,
          nextId: externalBase.rows.length
        });
      } else {
        setBase(externalBase);
      }
    }
  }

  const currentDraft =
    draftState !== null && draftState.base === base ? draftState : null;

  const draft = currentDraft?.state ?? base;
  const isDirty = draft !== base; // reference equality is sufficient
  // A failed persist leaves the panel clean but still re-savable, so the user can
  // retry after the error toast.
  const canSave = isDirty || saveFailed;
  const nextId = currentDraft?.nextId ?? base.rows.length;

  const {
    activeColor,
    setActiveColor,
    editingRowId,
    setEditingRowId,
    openColor,
    openEdit,
    isActiveSwatch,
    activeRow,
    activeColorValue
  } = useLegendSubPanels({
    disabled,
    rows: draft.rows,
    otherFill: draft.otherFill,
    otherOutline: draft.otherOutline
  });

  const setDraft = useCallback(
    (
      updater:
        | CustomCategoryState
        | ((prev: CustomCategoryState) => CustomCategoryState),
      nextIdOverride?: number
    ) => {
      const next = typeof updater === "function" ? updater(draft) : updater;
      setDraftState({
        base,
        state: next,
        nextId: nextIdOverride ?? nextId
      });
      const previewValue = mapStateToValue(next);
      previewedValueRef.current = previewValue;
      onPreview?.(previewValue);
    },
    [draft, base, nextId, onPreview]
  );

  const imageMode = useImage && isPOI;
  const maxSteps = Math.min(Math.max(colorSteps, MIN_STEP), MAX_STEP);

  const updateRow = (id: string, patch: Partial<CategoryRow>) =>
    setDraft((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (r.id === id ? { ...r, ...patch } : r))
    }));

  // Adding steps
  const handleAddStep = () => {
    // Add Step is capped at the active color scale step number (`maxSteps`).
    if (draft.rows.length >= maxSteps) return;
    const color = customCategoryPaletteColor(
      colorFamily,
      draft.rows.length,
      draft.rows.length + 1
    );
    setDraft(
      (prev) => ({
        ...prev,
        rows: [
          ...prev.rows,
          {
            id: `cat-${nextId}`,
            name: `Category ${prev.rows.length + 1}`,
            values: [],
            fillColor: color,
            outlineColor: color,
            image: null,
            imageName: null,
            imageAssetId: null,
            iconId: null
          }
        ]
      }),
      nextId + 1
    );
  };

  // Removing steps
  const handleRemoveStep = (id: string) => {
    if (draft.rows.length <= 1) return;
    // Clear the current color selection since removing a row changes the list.
    setActiveColor(null);
    setEditingRowId(null);
    setDraft((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => r.id !== id)
    }));
    // A deletion only ever lowers the count — it never raises a step number
    // that was already below the row count.
    const nextStep = stepCountForRows(draft.rows.length - 1);
    if (nextStep < colorSteps) {
      if (stepBeforeEdit === null) setStepBeforeEdit(colorSteps);
      emitStepCount(nextStep);
    }
  };

  // Resetting config to default
  const handleReset = () => {
    const resetConfig = onReset?.();
    if (resetConfig) {
      setDraftState(null);
      // The restored default owns the step count again.
      endEditingSession();
      emitStepCount(stepCountForRows(resetConfig.steps.length));
    } else {
      const next = mapValueToState(
        getDefaultCustomCategoryConfig(
          colorFamily,
          colorSteps ?? draft.rows.length,
          tableValues
        )
      );
      setDraftState({ base, state: next, nextId: next.rows.length });
      onSave?.(mapStateToValue(next));
    }
    setActiveColor(null);
    setEditingRowId(null);
    setUseImage(false);
    lastMarkersRef.current = null;
  };

  // Change to image or icon mode
  const handleToggleImage = (checked: boolean) => {
    setUseImage(checked);
    setEditingRowId(null);
    if (checked) {
      // Enabling Image while a category row's color picker is open swaps it for
      // that row's Marker Visual panel (matches Simple). The "Other" row has no
      // per-row image, so its picker is left as-is.
      setActiveColor((prev) =>
        prev && prev.rowId !== OTHER_ROW_ID
          ? { ...prev, target: "image" }
          : prev
      );
      const lastMarkers = lastMarkersRef.current;
      if (!lastMarkers) return;
      setDraft((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => {
          const marker = lastMarkers.get(r.id);
          return marker ? { ...r, ...marker } : r;
        })
      }));
    } else {
      const markers = new Map<string, RowMarkerFields>(
        draft.rows
          .filter((r) => r.image || r.iconId)
          .map((r) => [
            r.id,
            {
              image: r.image,
              imageName: r.imageName,
              imageAssetId: r.imageAssetId,
              iconId: r.iconId
            }
          ])
      );
      if (markers.size) lastMarkersRef.current = markers;
      setActiveColor(null);
      setDraft((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => ({
          ...r,
          image: null,
          imageName: null,
          imageAssetId: null,
          iconId: null
        }))
      }));
    }
  };

  // Saving config
  const handleSave = async () => {
    const value = mapStateToValue(draft);
    setDraftState(null);
    setActiveColor(null);
    setEditingRowId(null);
    // The lowered step is committed with the config, so there is nothing to undo.
    endEditingSession();
    const persisted = await onSave?.(value);
    setSaveFailed(persisted === false);
  };

  // Canceling edits config
  const handleCancel = async () => {
    // Read before the session ends; `base` is the only pre-change copy left.
    const staleBaseline = hasStaleBaseline ? mapStateToValue(base) : null;
    // Reverted edits take the step they changed back with them — otherwise the
    // store keeps a count the restored categories don't match.
    const restoreStep = stepBeforeEdit;
    setDraftState(null);
    endEditingSession();
    setActiveColor(null);
    setEditingRowId(null);
    if (staleBaseline) {
      // onRevert REPLACES onCancel when a step resize overwrote the store's
      // baseline: onCancel would only restore the resized config it left behind.
      // It also persists, so the step goes back FIRST — a half-restored store
      // would be written as the restored config paired with the resized step.
      if (restoreStep !== null) emitStepCount(restoreStep);
      const persisted = await onRevert?.(staleBaseline);
      // A failed write leaves the store ahead of the BE; keep the panel savable
      // so the user can push the restored config again.
      setSaveFailed(persisted === false);
      return;
    }
    onCancel?.();
    if (restoreStep !== null) emitStepCount(restoreStep);
    setSaveFailed(false);
  };

  const editingRow = draft.rows.find((r) => r.id === editingRowId) ?? null;
  const valuesAssignedElsewhere = editingRow
    ? draft.rows.filter((r) => r.id !== editingRow.id).flatMap((r) => r.values)
    : [];

  const handleActiveColorChange = (hex: string) => {
    if (!activeColor?.rowId) return;
    if (activeColor.rowId === OTHER_ROW_ID) {
      setDraft((prev) => ({
        ...prev,
        ...(activeColor.target === "outline"
          ? { otherOutline: hex }
          : { otherFill: hex })
      }));
      return;
    }
    updateRow(
      activeColor.rowId,
      activeColor.target === "outline"
        ? { outlineColor: hex }
        : { fillColor: hex }
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-[#e5e7eb] p-3">
      {/* Image toggle + Reset */}
      <div className="flex items-center justify-between">
        {isPOI ? (
          <button
            type="button"
            className="contents"
            // Don't let the toggle's press dismiss the color picker, so Image
            // swaps the open picker to a marker instead of closing it.
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <label className="flex items-center gap-2 text-xs text-txtHeading">
              <Switch
                checked={useImage}
                onCheckedChange={handleToggleImage}
                disabled={disabled}
                data-testid="ordinal-image-toggle"
              />
              Image
            </label>
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleReset}
          // Always available (for every dataset) — Reset returns to the active
          // color scale default regardless of whether there are pending edits.
          disabled={disabled}
          className="text-xs text-primary-teal disabled:opacity-50"
          data-testid="ordinal-reset"
        >
          <div className="flex flex-row items-center">
            <ResetIcon className="mr-1 inline h-3 w-3" />
            Reset
          </div>
        </button>
      </div>

      <LegendColumnHeaders
        imageMode={imageMode}
        strokeOnly={strokeOnly}
        withActions
      />

      {/* Category rows */}
      <div className="flex flex-col gap-2">
        {draft.rows.map((row) => (
          <LegendRow
            key={row.id}
            imageMode={imageMode}
            strokeOnly={strokeOnly}
            image={row.image}
            iconId={row.iconId}
            fillColor={row.fillColor}
            outlineColor={row.outlineColor}
            isFillActive={isActiveSwatch(row.id, ["fill", "image"])}
            isOutlineActive={isActiveSwatch(row.id, ["outline"])}
            disabled={disabled}
            onFillClick={() => openColor(row.id, imageMode ? "image" : "fill")}
            onOutlineClick={() => openColor(row.id, "outline")}
            fillTestId={`ordinal-fill-${row.id}`}
            outlineTestId={`ordinal-outline-${row.id}`}
            onEdit={() => openEdit(row.id)}
            editAriaLabel="Edit category"
            editTestId={`ordinal-edit-${row.id}`}
            onDelete={() => handleRemoveStep(row.id)}
            deleteAriaLabel="Delete category"
            deleteTestId={`ordinal-delete-${row.id}`}
            deleteDisabled={draft.rows.length <= 1}
          >
            <span className="truncate text-xs font-medium text-txtHeading">
              {row.name}
            </span>
            <span
              className="truncate text-[10px] text-grey"
              data-testid={`ordinal-values-count-${row.id}`}
            >
              {row.values.length ? `${row.values.length} Selected` : "No Value"}
            </span>
          </LegendRow>
        ))}
      </div>

      {/* Add Step */}
      <button
        type="button"
        onClick={handleAddStep}
        disabled={disabled || draft.rows.length >= maxSteps}
        className="flex items-center justify-center gap-1 py-1 text-xs font-medium text-primary-teal disabled:opacity-50"
        data-testid="ordinal-add-step"
      >
        + Add Step
      </button>

      <div className="h-px bg-gainsboro" />

      {/* Other row (system-managed) */}
      <LegendOtherRow
        testIdPrefix="ordinal"
        strokeOnly={strokeOnly}
        fillColor={draft.otherFill}
        outlineColor={draft.otherOutline}
        isFillActive={isActiveSwatch(OTHER_ROW_ID, ["fill"])}
        isOutlineActive={isActiveSwatch(OTHER_ROW_ID, ["outline"])}
        disabled={disabled}
        onFillClick={() => openColor(OTHER_ROW_ID, "fill")}
        onOutlineClick={() => openColor(OTHER_ROW_ID, "outline")}
      />

      {/* Cancel / Save */}
      <LegendActions
        disabled={disabled || !canSave}
        onCancel={handleCancel}
        onSave={handleSave}
      />

      <LegendRowActivePanel
        activeColor={activeColor}
        activeRow={activeRow}
        activeColorValue={activeColorValue}
        updateRow={updateRow}
        onColorChange={handleActiveColorChange}
        onClose={() => setActiveColor(null)}
      />

      {editingRow && (
        <EditCategoryPanel
          // Remount per row so the draft name/values re-init to the clicked
          // category — clicking another row's Edit without closing the panel
          // must show that row's name, not the previously opened one.
          key={editingRow.id}
          categoryName={editingRow.name}
          assignedValues={editingRow.values}
          attributeValues={allValues}
          valuesInOtherCategories={valuesAssignedElsewhere}
          isLoadingValues={isLoadingValues}
          onSave={(name, values) => updateRow(editingRow.id, { name, values })}
          onClose={() => setEditingRowId(null)}
        />
      )}
    </div>
  );
};

export default CustomCategoryConfig;
